import {
  MessageSquare,
  ShoppingBag,
  ShoppingCart,
  RefreshCw,
  RotateCcw,
  ArrowLeftRight,
} from 'lucide-react';
import type { SupportRecord, ProductRecord } from '../types';

export interface RecordViewConfigItem {
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  inProgressStatuses?: string[];
  defaultMode?: string;
  modes?: Array<{ key: string; label: string }>;
}

export const RECORD_VIEW_CONFIG: Record<string, RecordViewConfigItem> = {
  chat: {
    label: 'Chat',
    description: 'Conversation transcript and AI assistance',
    icon: MessageSquare,
  },
  orders: {
    label: 'Orders',
    description: 'Latest ecommerce activity and fulfillment status',
    icon: ShoppingBag,
    inProgressStatuses: ['Processing', 'On Hold', 'Awaiting Shipment'],
    defaultMode: 'overview',
    modes: [
      { key: 'overview', label: 'Overview' },
      { key: 'list', label: 'List' },
    ],
  },
  products: {
    label: 'Products',
    description: 'Catalog inventory and merchandising signals',
    icon: ShoppingCart,
    defaultMode: 'list',
    modes: [
      { key: 'list', label: 'List' },
      { key: 'carousel', label: 'Carousel' },
    ],
  },
  subscriptions: {
    label: 'Subscriptions',
    description: 'Renewals, pauses, and bundled plans',
    icon: RefreshCw,
    inProgressStatuses: ['Past Due', 'Paused', 'Pending Activation'],
    defaultMode: 'overview',
    modes: [
      { key: 'overview', label: 'Overview' },
      { key: 'list', label: 'List' },
    ],
  },
  returns: {
    label: 'Returns',
    description: 'Inbound logistics, inspections, and refunds',
    icon: RotateCcw,
    inProgressStatuses: ['Pending Inspection', 'Awaiting Label'],
    defaultMode: 'overview',
    modes: [
      { key: 'overview', label: 'Overview' },
      { key: 'list', label: 'List' },
    ],
  },
  exchanges: {
    label: 'Exchanges',
    description: 'Replacement requests and store credit issuance',
    icon: ArrowLeftRight,
    inProgressStatuses: ['Awaiting Pickup', 'Pending Inventory'],
    defaultMode: 'overview',
    modes: [
      { key: 'overview', label: 'Overview' },
      { key: 'list', label: 'List' },
    ],
  },
};

export const DEFAULT_RECORD_DATA: {
  orders: SupportRecord[];
  subscriptions: SupportRecord[];
  returns: SupportRecord[];
  exchanges: SupportRecord[];
  products: ProductRecord[];
} = {
  orders: [
    {
      id: 'ORD-1045',
      status: 'Fulfilled',
      amount: '$248.00',
      date: 'May 28, 2024',
      summary: 'Outdoor essentials bundle',
      customer: 'Jordan Lee',
      location: 'Austin, TX',
      metrics: [
        { label: 'Items', value: '3' },
        { label: 'Shipping', value: '2-day Express' },
      ],
      tags: ['VIP', 'Repeat'],
      nextAction: 'Share delivery ETA with customer',
      channel: 'Shopify',
      updatedAt: '2024-05-29T15:10:00Z',
    },
    {
      id: 'ORD-1044',
      status: 'Processing',
      amount: '$164.00',
      date: 'May 27, 2024',
      summary: 'Wellness refills + add-on tea set',
      customer: 'Mina Hart',
      location: 'Seattle, WA',
      metrics: [
        { label: 'Items', value: '5' },
        { label: 'Shipping', value: 'Standard' },
      ],
      tags: ['Subscription attach'],
      nextAction: 'Confirm inventory for tea set',
      channel: 'Amazon',
      updatedAt: '2024-05-28T12:20:00Z',
    },
    {
      id: 'ORD-1043',
      status: 'On Hold',
      amount: '$92.00',
      date: 'May 26, 2024',
      summary: 'Starter kit replacement',
      customer: 'Alex Romero',
      location: 'Chicago, IL',
      metrics: [
        { label: 'Items', value: '1' },
        { label: 'Reason', value: 'Payment review' },
      ],
      tags: ['Risk review'],
      nextAction: 'Confirm payment with customer',
      channel: 'Stateset POS',
      updatedAt: '2024-05-27T09:05:00Z',
    },
  ],
  subscriptions: [
    {
      id: 'SUB-4472',
      status: 'Active',
      amount: '$64.00 / mo',
      date: 'Renews Jun 21, 2024',
      summary: 'Wellness care plan \u00b7 12 deliveries left',
      customer: 'Mina Hart',
      metrics: [
        { label: 'Plan', value: 'Premium' },
        { label: 'Tenure', value: '14 months' },
      ],
      tags: ['AutoPay'],
      nextAction: 'Offer add-on hydration kit',
      channel: 'Recharge',
      updatedAt: '2024-05-30T09:45:00Z',
    },
    {
      id: 'SUB-4299',
      status: 'Past Due',
      amount: '$32.00 / mo',
      date: 'Failed May 24, 2024',
      summary: 'Coffee collective membership',
      customer: 'Theo Park',
      metrics: [
        { label: 'Skipped Orders', value: '2' },
        { label: 'Preferred Store', value: 'Nashville' },
      ],
      tags: ['Needs Outreach'],
      nextAction: 'Retry payment with backup card',
      channel: 'Stripe Billing',
      updatedAt: '2024-05-24T18:20:00Z',
    },
    {
      id: 'SUB-4181',
      status: 'Paused',
      amount: '$21.00 / mo',
      date: 'Paused until Jul 04, 2024',
      summary: 'Supplements subscribe & save',
      customer: 'Jamie Patel',
      metrics: [
        { label: 'Reason', value: 'Travel' },
        { label: 'Skips left', value: '1' },
      ],
      tags: ['Retention target'],
      nextAction: 'Send reactivation incentive',
      channel: 'Stateset Portal',
      updatedAt: '2024-05-22T11:10:00Z',
    },
  ],
  returns: [
    {
      id: 'RTN-3008',
      status: 'Pending Inspection',
      amount: '$128.00',
      date: 'Label issued May 29, 2024',
      summary: 'Defective humidifier \u00b7 prepaid label',
      customer: 'Jordan Lee',
      metrics: [
        { label: 'Carrier', value: 'UPS Ground' },
        { label: 'RMA Window', value: '4 days left' },
      ],
      tags: ['High priority'],
      nextAction: 'Expedite QA once received',
      channel: 'Loop Returns',
      updatedAt: '2024-05-29T16:05:00Z',
    },
    {
      id: 'RTN-2994',
      status: 'Refunded',
      amount: '$74.00',
      date: 'Completed May 26, 2024',
      summary: 'Wrong shade \u00b7 refund to store credit',
      customer: 'Samira Flores',
      metrics: [
        { label: 'Outcome', value: 'Store Credit' },
        { label: 'Processing Time', value: '2 days' },
      ],
      tags: ['CSAT 5/5'],
      nextAction: 'Invite to loyalty program',
      channel: 'Stateset Portal',
      updatedAt: '2024-05-26T13:30:00Z',
    },
  ],
  exchanges: [
    {
      id: 'EXC-1189',
      status: 'Awaiting Pickup',
      amount: '$0.00',
      date: 'Courier scheduled May 31, 2024',
      summary: 'Size swap \u00b7 leggings S \u2192 M',
      customer: 'Carter Mills',
      metrics: [
        { label: 'Replacement SKU', value: '#44819' },
        { label: 'Warehouse', value: 'Reno' },
      ],
      tags: ['Same-day pick up'],
      nextAction: 'Confirm courier arrival window',
      channel: 'Happy Returns',
      updatedAt: '2024-05-30T08:15:00Z',
    },
    {
      id: 'EXC-1182',
      status: 'Pending Inventory',
      amount: '$24.00 credit',
      date: 'Requested May 25, 2024',
      summary: 'Color swap + partial credit',
      customer: 'Paige Nguyen',
      metrics: [
        { label: 'Replacement SKU', value: '#99203' },
        { label: 'Inventory ETA', value: '2 days' },
      ],
      tags: ['Notify when back in stock'],
      nextAction: 'Reserve inventory and issue credit',
      channel: 'Stateset Portal',
      updatedAt: '2024-05-27T10:50:00Z',
    },
  ],
  products: [
    {
      id: 'SKU-44819',
      name: 'Luna Smart Bottle',
      price: '$89.00',
      status: 'In Stock',
      stock: 182,
      category: 'Wellness',
      summary: 'Self-cleaning hydration tracker',
      rating: 4.8,
      trend: '+18% WoW',
      badges: ['Top seller', 'Bundle ready'],
      nextAction: 'Promote hydration pack bundle',
      updatedAt: '2024-05-29T13:00:00Z',
      accent: 'from-sky-500 via-blue-500 to-indigo-500',
    },
    {
      id: 'SKU-77204',
      name: 'Orbit Home Diffuser',
      price: '$129.00',
      status: 'Low Stock',
      stock: 34,
      category: 'Home',
      summary: 'Dual-zone aromatherapy with app control',
      rating: 4.6,
      trend: '+9% WoW',
      badges: ['Back-in-stock'],
      nextAction: 'Expedite restock PO',
      updatedAt: '2024-05-28T09:20:00Z',
      accent: 'from-fuchsia-500 via-rose-500 to-amber-500',
    },
    {
      id: 'SKU-99011',
      name: 'Nova Recovery Kit',
      price: '$58.00',
      status: 'Preorder',
      stock: 0,
      category: 'Wellness',
      summary: 'Compression wraps + cooling gel packs',
      rating: 4.9,
      trend: '+41% WoW',
      badges: ['Launching soon'],
      nextAction: 'Send VIP waitlist email',
      updatedAt: '2024-05-27T20:45:00Z',
      accent: 'from-emerald-500 via-teal-500 to-cyan-500',
    },
  ],
};
